import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, clinicApi } from '@/lib/api';
import type { ClinicMember, UpdateMemberPayload } from '@/types/api';

const PEOPLE_KEY = ['people'] as const;

/**
 * Admin-only list of clinic members. The backend filters by the caller's active
 * clinic so we don't need to pass a clinicId — the JWT + middleware resolve it.
 */
export function useClinicMembers(opts: { enabled?: boolean } = {}) {
  const { enabled = true } = opts;
  return useQuery({
    queryKey: [...PEOPLE_KEY, 'list'],
    queryFn: () => clinicApi.get<ClinicMember[]>('users'),
    enabled,
    staleTime: 15_000,
  });
}

// No create hook: people join a clinic by invitation (see `useCreateInvitation`),
// because an account is global and its password belongs to its owner alone.

export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateMemberPayload }) =>
      clinicApi.patch<ClinicMember>(`users/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PEOPLE_KEY });
    },
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clinicApi.delete(`users/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PEOPLE_KEY });
    },
  });
}
